import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  IoAddOutline,
  IoCreateOutline,
  IoSettingsOutline,
  IoTrashOutline,
} from "react-icons/io5";
import {
  createField,
  deleteField,
  listFields,
  updateField,
} from "../../../api/fieldsApi";
import { getApiErrorMessage } from "../../../api/unwrapApiResponse";
import ConfirmDialog from "../../../components/dashboard/ConfirmDialog";
import EmptyState from "../../../components/dashboard/EmptyState";
import ErrorState from "../../../components/dashboard/ErrorState";
import LoadingState from "../../../components/dashboard/LoadingState";
import { useNotify } from "../../../components/ui/NotificationProvider";
import FieldInputRenderer from "../fields/FieldInputRenderer";
import PositionFormModal from "./PositionFormModal";
import {
  POSITION_FIELD_DEFS,
  getPositionTitle,
  groupFilterFields,
  groupPositionFields,
  positionGroupToForm,
  syncPositionCount,
} from "./positionFieldUtils";
import { usePositionsSection } from "./usePositionsSection";

const HEADER_FIELDS = [
  { key: "header_title", label: "Header title" },
  { key: "header_description", label: "Header description", fieldType: "textarea" },
  { key: "apply_form_title", label: "Apply form title" },
  { key: "apply_form_subtitle", label: "Apply form subtitle" },
];

export default function PositionsManager() {
  const notify = useNotify();
  const {
    sectionId,
    pageId,
    pageName,
    loading: sectionLoading,
    error: sectionError,
    reload: reloadSection,
  } = usePositionsSection();

  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [filterValues, setFilterValues] = useState({});
  const [headerValues, setHeaderValues] = useState({});
  const [savingSettings, setSavingSettings] = useState(false);

  const loadFields = useCallback(async () => {
    if (!sectionId) return;
    setLoading(true);
    setError("");
    try {
      const data = await listFields(sectionId);
      const list = Array.isArray(data) ? data : [];
      const sorted = list.sort(
        (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)
      );
      setFields(sorted);

      const nextFilters = {};
      sorted.forEach((field) => {
        if (/^filter_\d+$/.test(field.key || "")) {
          nextFilters[field.id] = field.value ?? "";
        }
      });
      setFilterValues(nextFilters);

      const nextHeader = {};
      sorted.forEach((field) => {
        if (HEADER_FIELDS.some((h) => h.key === field.key)) {
          nextHeader[field.id] = field.value ?? "";
        }
      });
      setHeaderValues(nextHeader);
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [sectionId]);

  useEffect(() => {
    if (sectionId) loadFields();
  }, [sectionId, loadFields]);

  const positions = useMemo(() => groupPositionFields(fields), [fields]);
  const filters = useMemo(() => groupFilterFields(fields), [fields]);
  const headerFieldRecords = useMemo(
    () =>
      HEADER_FIELDS.map((meta) => ({
        ...meta,
        field: fields.find((f) => f.key === meta.key) ?? null,
      })),
    [fields]
  );

  const hasSettingsChanges = useMemo(() => {
    const headerChanged = headerFieldRecords.some(
      ({ field }) =>
        field &&
        (headerValues[field.id] ?? "") !== (field.value ?? "")
    );
    if (headerChanged) return true;
    return filters.some(
      (field) => (filterValues[field.id] ?? "") !== (field.value ?? "")
    );
  }, [headerFieldRecords, headerValues, filters, filterValues]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return positions;
    return positions.filter((group) => {
      const form = positionGroupToForm(group);
      return (
        form.title.toLowerCase().includes(q) ||
        form.slug.toLowerCase().includes(q) ||
        form.category.toLowerCase().includes(q) ||
        form.location.toLowerCase().includes(q)
      );
    });
  }, [positions, query]);

  async function saveFieldRecord(field, value) {
    await updateField(field.id, {
      label: field.label,
      fieldType: field.fieldType,
      key: field.key,
      isRichText: field.isRichText,
      isRequired: field.isRequired,
      isPublished: field.isPublished,
      sortOrder: field.sortOrder,
      value: value ?? "",
    });
  }

  async function savePositionField(positionGroup, suffix, value) {
    const def = POSITION_FIELD_DEFS.find((d) => d.suffix === suffix);
    if (!def) return;

    const field = positionGroup.fields[suffix];
    const nextValue = value ?? "";

    if (field) {
      const currentValue = field.value ?? "";
      if (nextValue === currentValue) return;
      await saveFieldRecord(field, nextValue);
      return;
    }

    if (!nextValue) return;

    const baseOrder = 30 + positionGroup.index * 20;
    const defIndex = POSITION_FIELD_DEFS.findIndex((d) => d.suffix === suffix);

    await createField(sectionId, {
      key: `position_${positionGroup.index}_${suffix}`,
      label: `Position ${positionGroup.index} ${def.label}`,
      fieldType: def.fieldType,
      isRequired: false,
      sortOrder: baseOrder + (defIndex >= 0 ? defIndex : 0),
      value: nextValue,
    });
  }

  async function handleSavePosition(form) {
    if (!sectionId) return;
    setBusy(true);
    try {
      if (editingGroup) {
        const freshGroup =
          positions.find((p) => p.index === editingGroup.index) ?? editingGroup;
        const originalForm = positionGroupToForm(freshGroup);
        let savedAny = false;

        for (const def of POSITION_FIELD_DEFS) {
          const newValue = form[def.suffix] ?? "";
          const oldValue = originalForm[def.suffix] ?? "";
          if (newValue === oldValue) continue;

          await savePositionField(freshGroup, def.suffix, newValue);
          savedAny = true;
        }

        notify.success(savedAny ? "Position updated" : "No changes to save");
      } else {
        const indices = positions.map((p) => p.index);
        const nextIndex = indices.length ? Math.max(...indices) + 1 : 0;
        const baseOrder = 30 + nextIndex * 20;

        for (let j = 0; j < POSITION_FIELD_DEFS.length; j++) {
          const def = POSITION_FIELD_DEFS[j];
          await createField(sectionId, {
            key: `position_${nextIndex}_${def.suffix}`,
            label: `Position ${nextIndex} ${def.label}`,
            fieldType: def.fieldType,
            isRequired: false,
            sortOrder: baseOrder + j,
            value: form[def.suffix] ?? "",
          });
        }

        await syncPositionCount(sectionId, fields, [
          ...positions,
          { index: nextIndex, fields: {} },
        ]);
        notify.success("Position created");
      }

      setModalOpen(false);
      setEditingGroup(null);
      await loadFields();
    } catch (err) {
      notify.error(getApiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function confirmDelete() {
    if (!deleteTarget || !sectionId) return;
    setBusy(true);
    try {
      const ids = Object.values(deleteTarget.fields).map((f) => f.id);
      for (const id of ids) {
        await deleteField(id);
      }

      const remaining = positions.filter(
        (p) => p.index !== deleteTarget.index
      );
      await syncPositionCount(sectionId, fields, remaining);
      notify.success("Position deleted");
      setDeleteTarget(null);
      await loadFields();
    } catch (err) {
      notify.error(getApiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function saveAllSettings() {
    if (!hasSettingsChanges) return;
    setSavingSettings(true);
    try {
      for (const { field } of headerFieldRecords) {
        if (!field) continue;
        const next = headerValues[field.id] ?? "";
        if (next !== (field.value ?? "")) {
          await saveFieldRecord(field, next);
        }
      }
      for (const field of filters) {
        const next = filterValues[field.id] ?? "";
        if (next !== (field.value ?? "")) {
          await saveFieldRecord(field, next);
        }
      }
      notify.success("Changes saved");
      await loadFields();
    } catch (err) {
      notify.error(getApiErrorMessage(err));
    } finally {
      setSavingSettings(false);
    }
  }

  async function addFilter() {
    if (!sectionId) return;
    setBusy(true);
    try {
      const nextIndex = filters.length
        ? Math.max(...filters.map((f) => Number(f.key.split("_")[1]))) + 1
        : 0;

      await createField(sectionId, {
        key: `filter_${nextIndex}`,
        label: `Filter ${nextIndex}`,
        fieldType: "text",
        isRequired: false,
        sortOrder: 4 + nextIndex,
        value: "",
      });

      notify.success("Filter added");
      await loadFields();
    } catch (err) {
      notify.error(getApiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function removeFilter(field) {
    setBusy(true);
    try {
      await deleteField(field.id);
      notify.success("Filter removed");
      await loadFields();
    } catch (err) {
      notify.error(getApiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  if (sectionLoading) return <LoadingState message="Loading positions setup…" />;

  if (sectionError) {
    return (
      <div className="space-y-4">
        <ErrorState message={sectionError} onRetry={reloadSection} />
        {pageId ? (
          <Link
            to={`/cmsadmin/dashboard/pages/${pageId}/sections`}
            className="inline-block text-sm font-medium text-slate-700 underline"
          >
            Go to {pageName} sections
          </Link>
        ) : (
          <Link
            to="/cmsadmin/dashboard/pages"
            className="inline-block text-sm font-medium text-slate-700 underline"
          >
            Go to Pages
          </Link>
        )}
      </div>
    );
  }

  if (loading) return <LoadingState message="Loading positions…" />;
  if (error) return <ErrorState message={error} onRetry={loadFields} />;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-slate-900">
            Open positions
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            Manage job listings for the {pageName} page.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setSettingsOpen((v) => !v)}
            className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-gray-50"
          >
            <IoSettingsOutline className="h-5 w-5" />
            {settingsOpen ? "Hide settings" : "Page settings"}
          </button>
          <button
            type="button"
            onClick={() => {
              setEditingGroup(null);
              setModalOpen(true);
            }}
            className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-slate-800"
          >
            <IoAddOutline className="h-5 w-5" />
            Add position
          </button>
        </div>
      </div>

      {settingsOpen ? (
        <div className="space-y-4 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-slate-900">
            Careers page settings
          </h3>

          <div className="grid gap-4 md:grid-cols-2">
            {headerFieldRecords.map(({ key, label, fieldType, field }) =>
              field ? (
                <FieldInputRenderer
                  key={key}
                  field={{
                    ...field,
                    label,
                    fieldType: fieldType || field.fieldType,
                  }}
                  value={headerValues[field.id] ?? ""}
                  onChange={(next) =>
                    setHeaderValues((prev) => ({
                      ...prev,
                      [field.id]: next,
                    }))
                  }
                />
              ) : null
            )}
          </div>

          <div className="border-t border-gray-100 pt-4">
            <div className="mb-3 flex items-center justify-between gap-4">
              <p className="text-sm font-medium text-slate-700">
                Category filters
              </p>
              <button
                type="button"
                onClick={addFilter}
                disabled={busy}
                className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-gray-50 disabled:opacity-50"
              >
                <IoAddOutline className="h-4 w-4" />
                Add filter
              </button>
            </div>

            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {filters.map((field) => (
                <div
                  key={field.id}
                  className="rounded-xl border border-gray-100 bg-slate-50 p-3"
                >
                  <FieldInputRenderer
                    field={field}
                    value={filterValues[field.id] ?? ""}
                    onChange={(next) =>
                      setFilterValues((prev) => ({
                        ...prev,
                        [field.id]: next,
                      }))
                    }
                  />
                  <div className="mt-2 flex justify-end">
                    <button
                      type="button"
                      onClick={() => removeFilter(field)}
                      disabled={busy}
                      className="rounded-lg border border-red-200 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end border-t border-gray-100 pt-4">
            <button
              type="button"
              onClick={saveAllSettings}
              disabled={savingSettings || !hasSettingsChanges}
              className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
            >
              {savingSettings ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>
      ) : null}

      <input
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search by title, slug, category, or location…"
        className="w-full max-w-md rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-200"
      />

      {filtered.length === 0 ? (
        <EmptyState
          title={query ? "No matching positions" : "No positions yet"}
          description={
            query
              ? "Try a different search term."
              : "Create your first open position for the Careers page."
          }
          action={
            !query ? (
              <button
                type="button"
                onClick={() => {
                  setEditingGroup(null);
                  setModalOpen(true);
                }}
                className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white"
              >
                Add position
              </button>
            ) : null
          }
        />
      ) : (
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-slate-600">
                  Position
                </th>
                <th className="hidden px-4 py-3 text-left font-medium text-slate-600 md:table-cell">
                  Category
                </th>
                <th className="hidden px-4 py-3 text-left font-medium text-slate-600 lg:table-cell">
                  Location
                </th>
                <th className="hidden px-4 py-3 text-left font-medium text-slate-600 sm:table-cell">
                  Type
                </th>
                <th className="px-4 py-3 text-right font-medium text-slate-600">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((group) => {
                const form = positionGroupToForm(group);

                return (
                  <tr key={group.index} className="hover:bg-slate-50/80">
                    <td className="px-4 py-3">
                      <div className="min-w-0">
                        <p className="truncate font-medium text-slate-900">
                          {form.title || getPositionTitle(group)}
                        </p>
                        <p className="truncate text-xs text-slate-500">
                          /position/{form.slug || `position-${group.index}`}
                        </p>
                      </div>
                    </td>
                    <td className="hidden px-4 py-3 text-slate-600 md:table-cell">
                      {form.category || "—"}
                    </td>
                    <td className="hidden px-4 py-3 text-slate-600 lg:table-cell">
                      {form.location || "—"}
                    </td>
                    <td className="hidden px-4 py-3 text-slate-600 sm:table-cell">
                      {form.type || "—"}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setEditingGroup(group);
                            setModalOpen(true);
                          }}
                          className="rounded-lg border border-gray-200 p-2 text-slate-600 hover:bg-gray-50"
                          aria-label={`Edit ${form.title}`}
                        >
                          <IoCreateOutline className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeleteTarget(group)}
                          className="rounded-lg border border-gray-200 p-2 text-red-600 hover:bg-red-50"
                          aria-label={`Delete ${form.title}`}
                        >
                          <IoTrashOutline className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <PositionFormModal
        open={modalOpen}
        initialForm={editingGroup ? positionGroupToForm(editingGroup) : null}
        editingPositionKey={editingGroup?.index ?? null}
        isEdit={Boolean(editingGroup)}
        busy={busy}
        onClose={() => {
          setModalOpen(false);
          setEditingGroup(null);
        }}
        onSubmit={handleSavePosition}
      />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Delete position?"
        message={
          deleteTarget
            ? `This will permanently delete "${getPositionTitle(deleteTarget)}".`
            : ""
        }
        busy={busy}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
